import type { SiteCopy } from './types'

/**
 * Verde Vercelli 2 — English dictionary.
 *
 * A British-English mirror of ./it.ts, key for key (the content test checks
 * the structure and that fewer than 5 % of the long strings are left in
 * Italian). Register: polite and concrete, no superlatives. Every page opens
 * by naming the entity («Verde Vercelli di Andrea Vetri») and where it works
 * (Vercelli), and every FAQ answer repeats its subject so it still stands
 * alone in a search result or in the FAQPage JSON-LD.
 *
 * Proper nouns stay Italian: the company name, the wordmark sub «di Andrea
 * Vetri», place names (Vercelli, Santhià, Casale Monferrato, the Sesia, the
 * Po, the Bosco della Partecipanza). The only company figures are the ones
 * the Italian dictionary already carries: since 2011, 500+ projects, 4.9 on
 * 30 reviews. Service lists, steps and FAQ answers are new copy and need the
 * owner's sign-off before cutover, like their Italian originals.
 */
export const en = {
  meta: {
    locale: 'en',
    ogLocale: 'en_GB',
    siteName: 'Verde Vercelli',
    legalName: 'Verde Vercelli di Andrea Vetri',
    tagline: 'Garden maintenance around Vercelli',
  },

  a11y: {
    skipToContent: 'Skip to content',
    mainNav: 'Main navigation',
    footerNav: 'Footer navigation',
    langNav: 'Language selection',
    breadcrumbs: 'Breadcrumb',
    openMenu: 'Open the menu',
    closeMenu: 'Close the menu',
    themeToggle: 'Dark theme',
    reviews: 'Customer reviews',
    carouselPrev: 'Previous',
    carouselNext: 'Next',
    carouselGoTo: 'Go to',
  },

  nav: {
    services: 'Services',
    about: 'About us',
    numbers: 'Numbers',
    areas: 'Areas served',
    contact: 'Contact',
    cta: 'Free site visit',
    switchTo: 'Italiano',
    home: 'Home',
  },

  hero: {
    wordmarkSub: 'di Andrea Vetri',
    eyebrow: 'Verde Vercelli di Andrea Vetri · Vercelli',
    title: 'Garden maintenance, pruning and garden design in Vercelli and across the province.',
    subtitle:
      'Lawn mowing, hedge trimming, tree felling and pruning by tree climbing, stump grinding and topiary for homeowners, residential buildings and businesses around Vercelli, since 2011.',
    ctaPrimary: 'Request a free site visit',
    ctaPrimaryShort: 'Free site visit',
    scrollCue: 'Scroll',
  },

  manifesto: {
    eyebrow: 'About us',
    statement: 'A company focused on the *client* and their *preferences*.',
    lead:
      'Verde Vercelli di Andrea Vetri is a landscape care and maintenance company based in Vercelli, working since 2011 for homeowners, residential buildings, businesses and public authorities across the whole province.',
    pillarsTitle: 'Our values',
    pillars: [
      {
        title: 'Reliability',
        text: 'Deadlines kept, work done as agreed and a single point of contact from the site visit to the end of the job.',
      },
      {
        title: 'Innovation',
        text: 'Every year we invest in training, equipment and new working techniques.',
      },
      {
        title: 'Passion',
        text: 'We look after every garden with the care we would give our own.',
      },
    ],
    founderLine: 'Owner · since 2011',
  },

  servicesSection: {
    eyebrow: 'Services',
    title: 'What we do',
    intro:
      'Verde Vercelli di Andrea Vetri looks after gardens in Vercelli and across the province with eight services: garden design, tree felling, lawn mowing, post-work clean-up, hedge trimming, stump grinding, tree climbing and topiary. One team and a single point of contact, from the site visit to the final clean-up.',
    more: 'Find out more',
  },

  numeri: {
    eyebrow: 'Numbers',
    title: 'What we have done',
    years: { label: 'Years of experience', since: 'Since 2011' },
    projects: { label: 'Projects completed' },
    reviews: { label: 'Google reviews', cta: 'Read the reviews' },
  },

  faq: {
    eyebrow: 'Frequently asked questions',
    title: 'The questions we are asked most often',
    items: [
      {
        q: 'Is the site visit free?',
        a: 'Yes. The Verde Vercelli site visit is free in Vercelli and across the whole province: we come to see the garden and work out together the most suitable solutions for your green space.',
      },
      {
        q: 'Where do you work?',
        a: 'Verde Vercelli is based in Vercelli and works in every comune of the Vercelli plain, from the Po up to Gattinara and Lozzolo. In the Valsesia our partner Verde Valsesia does the work.',
      },
      {
        q: 'When will you get back to me?',
        a: 'Verde Vercelli replies within 24 working hours (Monday–Friday, 8am–6pm) to anyone who writes through the website or on WhatsApp, to arrange a date for the free site visit together.',
      },
      {
        q: 'Do you fell trees in tight spaces too?',
        a: 'Yes. When a tall tree stands in a courtyard, right next to a house or in a garden that no vehicle can reach, Verde Vercelli fells it by tree climbing: the arborist climbs the tree with ropes and a harness, works from above and takes the tree down section by section.',
      },
      {
        q: 'Who disposes of grass cuttings and branches?',
        a: 'Verde Vercelli does: at the end of the job the team gathers up grass cuttings, branches and timber, loads them and takes them away, leaving the area clean.',
      },
      {
        q: 'Do you work for residential buildings, businesses and public authorities?',
        a: 'Yes. Verde Vercelli looks after the green spaces of homeowners, residential buildings, businesses and public authorities in Vercelli and across the province.',
      },
      {
        q: 'Can I have a scheduled maintenance contract?',
        a: 'Yes. Verde Vercelli offers routine maintenance contracts for private gardens, residential buildings, businesses and public authorities, tailored to what each one needs.',
      },
    ],
  },

  contact: {
    eyebrow: 'Get in touch',
    title: "Let's talk about your garden",
    pageTitle: 'Request a free site visit',
    intro: 'A free site visit in Vercelli and across the whole province.',
    note: 'We reply within 24 working hours (Mon–Fri, 8am–6pm).',
    formUnavailable: 'The form is unavailable right now. Please write to us at',
    /**
     * The shared richieste form in English: every key of
     * `ContactFormStrings`, so no Italian default can leak through (the
     * content test checks the key set). English has no tu/Lei split, so
     * both formality variants carry the same text. The data controller's
     * name and address are not strings here: the form reads them from the
     * sede (Verde Vercelli di Andrea Vetri, Vercelli).
     */
    form: {
      stepLabels: {
        tu: ['Your details', 'What you need', 'When'],
        lei: ['Your details', 'What you need', 'When'],
      },
      stepAria: 'Go to step {n}: {label}',
      headings: {
        tu: ["Let's start: your details", 'What do you need?', 'When would you like the site visit?'],
        lei: ["Let's start: your details", 'What do you need?', 'When would you like the site visit?'],
      },
      subheadings: {
        tu: [
          'Tell us how to reach you — it only takes a few seconds.',
          'The service you need and where the property is.',
          'Let us know the days and times when you are available for the site visit at your property.',
        ],
        lei: [
          'Tell us how to reach you — it only takes a few seconds.',
          'The service you need and where the property is.',
          'Let us know the days and times when you are available for the site visit at your property.',
        ],
      },
      headingError: 'Please fill in the missing details',
      subheadingError: 'Check the fields highlighted in red to continue.',
      clientTypeLabel: 'Who is making the request?',
      clientTypes: { privato: 'Private', azienda: 'Business', condominio: 'Residential building' },
      condominioRoleLabel: 'In what capacity?',
      condominioRoles: { condomino: "I'm a resident", amministratore: "I'm the building manager" },
      nameLabelAzienda: 'Business name',
      nameLabelAmministrazione: 'Management company name',
      // Example names of Italian firms, as the visitor would type them.
      namePlaceholderAzienda: 'E.g. Giardini Rossi S.r.l.',
      namePlaceholderAmministrazione: 'E.g. Amministrazione Bianchi',
      emailChecking: 'Checking',
      emailValid: 'Valid email',
      emailInvalid: 'Invalid email',
      // Rendered as "Did you mean <suggestion>?".
      emailSuggestion: 'Did you mean',
      phonePrefixAria: 'International dialling code',
      serviceLabel: 'Service required',
      serviceOptions: {
        none: '— Select —',
        sopralluogo: 'Site visit request',
        manutenzione: 'Garden maintenance',
        progettazione: 'Garden design',
        potatura: 'Pruning / tree felling',
        irrigazione: 'Irrigation system',
        altro: 'Other',
      },
      addressLabel: 'Address',
      addressPlaceholder: 'Corso Libertà 10',
      zipLabel: 'Postcode',
      zipPlaceholder: '13100',
      cityLabel: 'Town',
      cityPlaceholder: 'Start typing the town…',
      requestLabel: { tu: 'Tell us about your request', lei: 'Tell us about your request' },
      requestPlaceholder: 'E.g. hedge trimming, removing a stump…',
      daysLabel: {
        tu: 'Days when you are at home for the visit',
        lei: 'Days when you are at home for the visit',
      },
      days: {
        lunedi: 'Mon',
        martedi: 'Tue',
        mercoledi: 'Wed',
        giovedi: 'Thu',
        venerdi: 'Fri',
        sabato: 'Sat',
      },
      timeLabel: 'Preferred time',
      timeFrom: 'From',
      timeTo: 'To',
      contactMethodLabel: {
        tu: 'How should we get in touch to confirm?',
        lei: 'How should we get in touch to confirm?',
      },
      contactMethods: { telefono: 'Phone', whatsapp: 'WhatsApp', email: 'Email' },
      // Rendered as "intro controller <owner>, basedIn <address>, reachable <email>. purpose revoke readMore".
      privacyConsent: {
        // A «presa visione», not a consent (mirrors shared/contact-form/strings.ts).
        intro:
          'I confirm that I have read the privacy notice on the processing of my personal data under Regulation (EU) 2016/679 (GDPR).',
        controller: 'The data controller is',
        basedIn: 'based at',
        reachable: 'who can be contacted at the email address',
        purpose:
          'Your data will be processed to handle your request; any hand-off to another company of the AVMG network happens only with your specific consent.',
        revoke: 'I may ask for access to my data or for its deletion at any time.',
        readMore: 'Read the full privacy notice →',
      },
      // Rendered as "part1 <b>part2</b>part3 <b>stopWord</b> part4 more." — the
      // form puts no space between part2 and part3, so part3 opens with one.
      whatsappConsent: {
        part1: 'I agree to receive service messages on',
        part2: 'WhatsApp',
        part3:
          ' (appointment confirmations, reminders of scheduled work, updates on the services I have requested), a service provided by Meta Platforms Ireland Limited. I may withdraw my consent at any time by sending the word',
        stopWord: 'STOP',
        part4: 'to the WhatsApp number the messages come from.',
        more: 'Find out more',
      },
      back: '← Back',
      next: 'Next →',
      success: {
        title: 'Request sent',
        body: 'We have emailed you a confirmation with a summary of your request.',
        note: 'We will get back to you within 24 working hours (Mon–Fri, 8am–6pm).',
        again: 'Send another request',
      },
      error: 'Something went wrong. Please try again or give us a call.',
      validation: {
        clientType: 'Please choose the type of client',
        condominioRole: 'Please say whether you are a resident or the building manager',
        nameRequired: 'Please enter your name',
        emailInvalid: 'Invalid email',
        phoneInvalid: 'Invalid phone number',
        serviceRequired: 'Please choose a service',
        addressRequired: 'Please enter the address',
        cityRequired: 'Please enter the town',
        zipRequired: 'Please enter the postcode',
        contactRequired: 'Please choose how you would like to be contacted',
        privacyRequired: 'Please confirm that you have read the privacy notice',
        whatsappRequired: 'We need your explicit consent to contact you on WhatsApp',
        whatsappMobile: 'WhatsApp requires a mobile number',
      },
      validationMessages: {
        emailRequired: 'Please enter an email address.',
        emailSyntax: 'The email format is not valid.',
        emailDisposable:
          'Disposable email addresses are not accepted. Please use a personal or work address.',
        emailGeneric: 'The email is not valid.',
        phoneRequired: 'Please enter a phone number.',
        phoneFormat: 'The phone number format is not recognised.',
        phoneImpossible: 'The phone number does not look right (wrong length).',
        phoneInvalid: 'The phone number is not valid for the selected country.',
        phoneNotMobile: 'Messages on WhatsApp need a mobile number.',
        phoneItPrefix: 'Italian numbers start with 3 (mobile) or 0 (landline).',
        phonePrefixGeneric: 'The first digit must be {digits}.',
      },
      i18n: {
        headline: 'Contact us',
        intro: 'Free site visit in Vercelli and across the province.',
        name: 'Full name',
        email: 'Email',
        phone: 'Phone',
        submit: 'Send request',
      },
    },
  },

  zone: {
    eyebrow: 'Areas served',
    title: 'Vercelli and its province',
    intro:
      'Verde Vercelli di Andrea Vetri is based in Vercelli and works across the Vercelli plain, from the Po up to Gattinara, for homeowners, residential buildings, businesses and public authorities. Below you will find the map of the area and the full list of the towns we serve; the Valsesia is looked after by our partner Verde Valsesia.',
    baseLabel: 'our base',
    headers: {
      comune: 'Town',
      note: 'Distinguishing marks',
    },
    notes: {
      // One checked fact per town (sources: it.wikipedia / official sites, read 2026-09-28),
      // keyed by data/servedComuni.ts; a test requires every key in both locales.
      'albano-vercellese': 'Headquarters and entrance of the Parco naturale delle Lame del Sesia',
      'alice-castello': 'Eighth-century Lombard tombs and a castle already recorded in 1173',
      arborio: 'The village that gives its name to the Arborio rice variety',
      'asigliano-vercellese': "Maratelli rice, one of Italy's oldest varieties, selected here in 1914",
      balocco: "The Cascina Bella Luigina test tracks, Alfa Romeo's former proving ground",
      bianze: 'Ancient Blanzate, a fortified Roman camp with defensive walls 1.5 m thick',
      'borgo-d-ale': "The Bella di Borgo d'Ale peach, a Slow Food Presidium",
      'borgo-vercelli': "Every 8 September to Casale's Duomo, fulfilling a vow made in the 1403 plague",
      buronzo: 'A rare example of a consortial castle, first recorded in a deed of 1039',
      caresana: "The Corsa dei buoi ox race, held the Sunday after St George's Day (23 April)",
      caresanablot: 'The “Blot” in its name: a contraction of Belotto, in use since at least 1744',
      carisio: 'On the doorstep of the Riserva naturale della Garzaia di Carisio',
      'casale-monferrato': 'The 1595 synagogue, one of the finest Piedmontese Baroque synagogues',
      'casanova-elvo': 'A castle recorded since 1170, with a round tower and spiral staircase',
      cigliano: 'The Torre Civica bell tower of 1733, built under the architect Castelli',
      collobiano: 'A castle with swallowtail-crenellated towers and a rare octagonal tower',
      costanzana: 'Towards Desana, the Madonna delle Grazie sanctuary, already there in the early 1500s',
      crescentino: "Santuario Madonna del Palazzo: the first parish church rose here, at Sant'Eusebio's wish",
      crova: 'Rice on more than 95% of the usable farmland',
      desana: 'The “Desana treasure”: 51 precious objects from a late-Roman villa',
      'fontanetto-po': 'The Mulino di San Giovanni, a watermill built after the channel was dug in 1465',
      formigliana: 'Recorded as early as 882, when Charles the Fat gave it to the Church of Vercelli',
      gattinara: 'Gattinara wine, DOC since 1967 and DOCG since 1990, from nebbiolo (spanna) grapes',
      ghislarengo: 'In the Assunta parish church, a silver reliquary holding a piece of the True Cross',
      greggio: 'Partly within the Parco naturale delle Lame del Sesia',
      lamporo: 'The Madonna di Loreto chapel, built straddling the Lamporo canal',
      lenta: 'The castle-monastery, enclosing a Benedictine convent of nuns founded in 1127',
      lignana: 'Cascina Veneria: the main scenes of “Riso amaro” were shot here in 1949',
      'livorno-ferraris': 'Named since 1924 after Galileo Ferraris, the scientist born here',
      lozzolo: '13th-century castle: the Sala degli Amici, frescoed by Avondo and fellow artists in 1866',
      moncrivello: 'The castle was the favourite residence of Iolanda di Francia, who died there in 1478',
      'motta-de-conti': 'Its only hamlet, Mantie, lies across the Sesia and can be reached only from Lomellina',
      novara: "Antonelli's dome of San Gaudenzio: 121 metres of brick and lime, with no iron",
      olcenengo: 'The Via Francigena passes through, from San Germano Vercellese towards Vercelli',
      oldenico: 'Part of its territory lies within the Parco naturale delle Lame del Sesia',
      'palazzolo-vercellese': 'Home of the csenta, the cake of the “incanto delle torte” in late October',
      pertengo: 'By tradition, the name comes from the motto “Pro Te Teneo”',
      pezzana: 'Santuario delle Grazie: a 17th-century altarpiece with Cardinal Maurizio di Savoia at prayer',
      prarolo: 'Church of the Assunta, built in 1628 at the behest of Cardinal Federico Borromeo',
      'quinto-vercellese': 'Santi Nazario e Celso: among its medieval frescoes, a saint reading with spectacles',
      rive: 'In 1268 its castle was a stronghold of the Ghibelline Tizzoni, on the border with Casale',
      roasio: "“Il paese con la valigia”, with emigrants in 50 countries: Museo dell'emigrante since 2001",
      ronsecco: 'Sanctuary of the Viri Veri: the name is a dialect corruption of the Latin Villae Veteris',
      rovasenda: 'The 10th-century castle with its thousand-year-old tower',
      salasco: 'Selve, where “Riso amaro” (1949) with Silvana Mangano and Vittorio Gassman was filmed',
      'sali-vercellese': 'The castle, first recorded in 1268, probably built as a refuge for the harvest',
      saluggia: 'The village of beans, where the Bean King and Queen reign over Carnival',
      'san-germano-vercellese': 'The dome of the parish church of San Germano, designed by Vittone (1760)',
      'san-giacomo-vercellese': 'Grew from farmsteads on the Rovasenda stream; called Cascine San Giacomo until 1964',
      santhia: "The Carnevale Storico, Piedmont's oldest carnival, documented since at least 1328",
      stroppiana: 'The Fondazione Marazzato: vintage lorries housed in a former textile mill',
      tricerro: 'A name recalling the three Turkey oaks that once stood out here',
      trino: 'The Bosco della Partecipanza, granted in 1202 to an association of families',
      'tronzano-vercellese': 'A free borough since 1256, formed by uniting the two Tronzano villages',
      vercelli: "Sant'Andrea (1219–1227), one of the very first examples of transalpine Gothic in Italy",
      villarboit: 'The two aqueduct bridges carrying the Canale Cavour over the Rovasenda and Marchiazza',
      villata: 'Founded by people from Casalvolone fleeing raids over land disputed by Vercelli and Novara',
    },
    allProvince: 'The Vercelli plain, from the Po to Gattinara',
    allComuniTitle: 'Every town we serve',
    partner: { text: 'In the Valsesia, from Serravalle Sesia to Alagna, the work is done by our partner', link: 'Verde Valsesia' },
    neighbours:
      'Beyond the provincial boundary Verde Vercelli also works in Novara, across the Sesia, and in Casale Monferrato, on the banks of the Po in the province of Alessandria. If your town is not listed, write to us: we assess the distance at the site visit.',
    home: {
      intro: 'One base in Vercelli, one team that covers the Vercelli plain, from the Po to Gattinara.',
      figures: {
        places: 'towns of the Vercelli plain served, from Gattinara to the Po',
        area: 'the area of the province of Vercelli',
        rice: 'of rice paddies in the province of Vercelli',
      },
      more: 'All the areas we cover',
    },
    map: {
      desc: 'Map of the Vercelli plain, from the Po to Gattinara, with the province of Vercelli highlighted within its boundary: the rice paddies field by field, the woods along the rivers, the Canale Cavour, the Baraggia, the Lame del Sesia, the Bosco della Partecipanza and the Parco del Po. It marks our base in Vercelli and every town we serve.',
      legendTitle: 'Key',
      legend: {
        base: 'Our base',
        comune: 'Town served',
        water: 'Rivers',
        paddy: 'Rice paddies',
        wood: 'Woods',
        province: 'Province of Vercelli',
      },
      reliefCredit: 'Relief: EU-DEM (Copernicus, EU) and SRTM (USGS) via Terrain Tiles',
      osmCredit: 'Land cover, water and roads © OpenStreetMap contributors',
    },
  },

  footer: {
    addressLabel: 'Office',
    emailLabel: 'Email',
    phoneLabel: 'Telephone',
    pivaLabel: 'VAT no.',
    reaLabel: 'REA',
    pecLabel: 'PEC',
    hoursLabel: 'Opening hours',
    reviewsLabel: 'Reviews',
    network: 'Part of the AVMG network · Verde Valsesia',
    poweredBy: 'Powered by AVMG',
    links: {
      privacy: 'Privacy',
      cookie: 'Cookies',
      dataDeletion: 'Data deletion',
      credits: 'Credits',
    },
    socials: {
      instagram: 'Instagram',
      facebook: 'Facebook',
      google: 'Google reviews',
      linkedin: 'LinkedIn',
    },
    rights: 'All rights reserved',
  },

  notFound: {
    title: 'Page not found',
    text:
      'The page you were looking for no longer exists, or the address was typed differently. From the Verde Vercelli home page you will find the eight services, the areas served in Vercelli and the province, and the form to request a site visit.',
    backHome: 'Back to the home page',
  },

  seo: {
    home: {
      title: 'Verde Vercelli — Garden maintenance in Vercelli',
      description:
        'Verde Vercelli di Andrea Vetri: garden design, lawn mowing, hedges, tree felling by tree climbing and stump grinding in Vercelli. Since 2011.',
      ogAlt: 'Aerial view of a wood, a frame from the Verde Vercelli opening video',
    },
    zone: {
      title: 'Areas served — Vercelli and province | Verde Vercelli',
      description:
        'The towns Verde Vercelli serves: Vercelli, Borgo Vercelli, Trino, Santhià, Crescentino, Gattinara and the province, plus Novara and Casale Monferrato.',
      ogAlt: 'Aerial view of a wood near Vercelli, a frame from the Verde Vercelli video',
    },
    contatti: {
      title: 'Contact and free site visit | Verde Vercelli',
      description:
        'Verde Vercelli: a free site visit for gardens, pruning and tree felling in Vercelli and the province. We reply within 24 working hours.',
      ogAlt: 'Verde Vercelli — free site visit request',
    },
    privacy: {
      title: 'Privacy notice | Verde Vercelli',
      description:
        'How Verde Vercelli di Andrea Vetri processes the personal data collected by the website and the contact form, and how to exercise your rights.',
      ogAlt: 'Verde Vercelli — privacy notice',
    },
    cookie: {
      title: 'Cookie policy | Verde Vercelli',
      description:
        'Which cookies and third-party tools the Verde Vercelli website uses, what they are for and how to change your choices at any time.',
      ogAlt: 'Verde Vercelli — cookie policy',
    },
    dataDeletion: {
      title: 'Data deletion | Verde Vercelli',
      description:
        'How to ask Verde Vercelli to delete the personal data collected through the website, the contact form or WhatsApp, and how quickly we respond.',
      ogAlt: 'Verde Vercelli — data deletion',
    },
    crediti: {
      title: 'Photo credits and licences | Verde Vercelli',
      description:
        'Authors, licences and sources of the photographs and videos used on the Verde Vercelli website, together with the licences of the typefaces.',
      ogAlt: 'Verde Vercelli — photo credits',
    },
    notFound: {
      title: 'Page not found | Verde Vercelli',
      description:
        'The page you were looking for does not exist on the Verde Vercelli website. From the home page you will find the services, areas served and contacts.',
      ogAlt: 'Verde Vercelli — page not found',
    },
  },

  pages: {
    progettazione: {
      name: 'Garden design',
      short:
        'From concept to planting. Soil survey, plant selection, landscape integration and irrigation systems.',
      heroTitle: 'Garden design in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri designs new gardens and reworks existing ones in Vercelli and across the province: from concept to planting, with the soil survey, plant selection and irrigation agreed with the client at the design stage.',
      includesTitle: 'What it includes',
      includes: [
        'Site visit and survey to weigh up together the best solutions for your needs.',
        'Supply and installation of a robotic mower to make looking after your lawn easier.',
        'Design with a site plan and a planting list, chosen for the soil and for how easy the garden will be to look after.',
        'Testing of the installed systems and the finishing touches that make the garden your own, with outdoor furniture.',
        'Irrigation system and drainage built into the design, before the lawn goes down.',
        'Seeding or natural roll-out turf, and the first cut.',
      ],
      detailTitle: 'The design starts from your needs',
      detailText:
        "The gardens we create are conceived as a space made for the client, a frame for their home. We start from the client's needs and from the wish to renew their garden and make the most of it in terms of aspect, easy upkeep in the years ahead and sustainability.",
      faq: [
        {
          q: 'How long does it take to settle on a design?',
          a: 'We aim to work out the best choices quickly, so that we can give our clients a final solution and plan the work around their needs.',
        },
        {
          q: 'Can I keep the plants I already have?',
          a: 'Almost always: during the site visit Verde Vercelli assesses every existing plant and keeps it in the design if it is healthy and suits the new garden.',
        },
        {
          q: 'What is the best season to create a new garden?',
          a: 'Verde Vercelli concentrates seeding and planting in spring and autumn, when the ground on the Vercelli plain can be worked and plants have time to root before the heat or the frost. The exact dates are set in the quote.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Garden design in Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli designs and builds gardens in Vercelli and across the province: soil survey, plant selection, irrigation and planting.',
        ogAlt: 'A frame from the Verde Vercelli video on garden design',
      },
    },

    abbattimento: {
      name: 'Tree felling',
      short:
        'Safe tree felling, timber removal. Work in confined spaces.',
      heroTitle: 'Tree felling in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri fells trees in Vercelli and across the province even where space is tight: by tree climbing, section by section, with each piece lowered to the ground and the timber taken away at the end. We work in private gardens, residential courtyards and business premises.',
      includesTitle: 'What it includes',
      includes: [
        'Assessment of the tree and its surroundings: roofs, overhead lines, fences, access.',
        'Sectional felling by tree climbing, with pieces lowered under control where there is no room for them to fall.',
        'Straight felling with a planned direction of fall, where space allows.',
        'Protection of the lawn, paths and structures in the work area.',
        'Cutting up, loading and disposal of timber and branches.',
        'Stump grinding, on request, to reclaim the area.',
      ],
      detailTitle: 'When the tree cannot come down in one piece',
      detailText:
        'When a tall tree has to be felled in a confined space — a garden, a courtyard, a public area — there is no room to drop it whole, and often no vehicle can get in. Verde Vercelli works by tree climbing: the arborist climbs the tree with ropes and a harness, works from above and fells it section by section, lowering each piece to the planned spot. The result is a felling with no impact on roofs, fences or nearby plants, and no ruts in the lawn.',
      faq: [
        {
          q: 'Do I need permission to fell a tree?',
          a: 'It depends on the municipality and on any restrictions on the tree or the site: several municipalities around Vercelli regulate tree felling through their own green-space rules. Verde Vercelli tells you at the site visit what to check, but the application remains the owner\'s responsibility.',
        },
        {
          q: 'What happens to the timber?',
          a: 'Verde Vercelli cuts up the timber, loads it and takes it away with the branches at the end of the job. If you would rather keep some of the wood, we agree that before we start.',
        },
        {
          q: 'Do you remove the stump as well?',
          a: 'On request, yes: after felling, Verde Vercelli grinds the stump below ground level with a dedicated stump grinder, so the area can be reseeded or replanted.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Tree felling in Vercelli | Verde Vercelli',
        description:
          'Tree felling by tree climbing in Vercelli and the province: Verde Vercelli works in confined spaces and takes the timber away.',
        ogAlt: 'A frame from the Verde Vercelli video on tree felling',
      },
    },

    taglioPrato: {
      name: 'Lawn mowing',
      short:
        'Lawn mowing, aeration, feeding and scheduled care tailored to private gardens, residential buildings and commercial sites.',
      heroTitle: 'Lawn mowing and lawn care in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri mows and looks after the lawns of private gardens, residential buildings and commercial sites in Vercelli and across the province: scheduled mowing, aeration, feeding and overseeding, at intervals set for your lawn.',
      includesTitle: 'What it includes',
      includes: [
        'Mowing with collection and removal of the grass, at the agreed interval.',
        'Edging along beds, paths and fences.',
        'Aeration to remove thatch and let the soil breathe.',
        'Seasonal feeding matched to the lawn.',
        'Overseeding of thin or damaged patches.',
        'A planned schedule of visits for the whole season.',
      ],
      detailTitle: 'A lawn is looked after by the calendar',
      detailText:
        'On the Vercelli plain summers are hot and humid, and the lawn grows at different rates from May to September: a late cut stresses it, a cut that is too low scorches it. That is why Verde Vercelli works to a schedule agreed at the start of the season, with the mowing interval, aeration and feeding set for your lawn rather than a generic cycle. For residential buildings and commercial sites the schedule becomes a scheduled-care contract with a single quote for the year.',
      faq: [
        {
          q: 'How often do you mow the lawn?',
          a: 'Verde Vercelli agrees the mowing interval during the free site visit; it depends on the season, the aspect and how much the lawn is watered. The dates go into a schedule, so you know in advance when we will be there.',
        },
        {
          q: 'Do you take the cut grass away?',
          a: 'Yes. Verde Vercelli collects the grass at the end of each visit and takes it away.',
        },
        {
          q: 'Do you do one-off cuts too?',
          a: 'Yes: Verde Vercelli also carries out one-off cuts, for example after a period away. For lawns looked after all season, the scheduled-care contract gathers the visits into a single quote and sets the dates in advance.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Lawn mowing in Vercelli | Verde Vercelli',
        description:
          'Lawn mowing, aeration, feeding and scheduled care for homes, residential buildings and businesses: Verde Vercelli works in Vercelli and the province.',
        ogAlt: 'A frame from the Verde Vercelli video on lawn mowing',
      },
    },

    pulizia: {
      name: 'Post-work clean-up',
      short:
        'Clean-up after every job, disposal of grass cuttings and branches, a tidy site restored.',
      heroTitle: 'Post-work clean-up in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri ends every job in Vercelli and across the province by cleaning up the area: collecting and disposing of grass cuttings and branches, clearing paths and courtyards, leaving the garden tidy again. The same service is also available on its own, for example for leaf clearance in autumn.',
      includesTitle: 'What it includes',
      includes: [
        'Collection of grass cuttings, branches, leaves and pruning waste.',
        'Blowing clear of paths, courtyards, pavements and car parks.',
        'Loading, transport and disposal of the waste material.',
        'A tidy site restored: edges, beds and trampled areas put back in order.',
        'A final check of the area with you or the building\'s contact person.',
      ],
      detailTitle: 'The job ends when the area is clean',
      detailText:
        'Pruning or felling always leaves something on the ground: leaves, twigs, sawdust, grass on the paths. For Verde Vercelli the clean-up is part of the job, not an extra to remember: the team collects everything, takes the material away and goes over the surfaces before leaving, so the garden, courtyard or car park can be used again the same day. That is why we also offer it on its own to anyone who needs an area put back in order.',
      faq: [
        {
          q: 'Is the clean-up included in the work?',
          a: 'Yes. Every Verde Vercelli job ends with collecting the material and cleaning the area.',
        },
        {
          q: 'Do you clear leaves in autumn too?',
          a: 'Yes. Verde Vercelli clears leaves in Vercelli and across the province, as a one-off job or as part of a scheduled maintenance contract, with loading and disposal of the material.',
        },
        {
          q: 'Do you clean the courtyards and car parks of residential buildings and businesses?',
          a: 'Yes. Verde Vercelli cleans the paths, courtyards and car parks of residential buildings and business premises after every job on the green spaces and, on request, to a schedule; the times are agreed with the building manager or the office in charge of the premises.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Post-work clean-up and green waste | Verde Vercelli',
        description:
          'Verde Vercelli collects and disposes of grass cuttings, branches and leaves and tidies gardens, courtyards and car parks in Vercelli and the province.',
        ogAlt: 'A frame from the Verde Vercelli video on the clean-up after a job',
      },
    },

    siepi: {
      name: 'Hedge trimming',
      short:
        'Ornamental and fruit pruning of hedges, shrubs, fruit trees and tall trees. Specific techniques for every species.',
      heroTitle: 'Hedge trimming and pruning in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri trims hedges and prunes shrubs, fruit trees and tall trees in Vercelli and across the province, with the technique and timing suited to each species, for private gardens, residential buildings and businesses.',
      includesTitle: 'What it includes',
      includes: [
        'Trimming hedges to shape, on the sides and the top, with the cuts made at the right time for the species.',
        'Ornamental pruning of shrubs and flowering bushes.',
        'Fruit pruning of the trees in your garden.',
        'Pruning of tall trees, by tree climbing where a cherry picker cannot reach.',
        'Reduction of hedges that have grown too tall or too wide.',
        'Collection and disposal of branches at the end of the job.',
      ],
      detailTitle: 'Every species has its own cut',
      detailText:
        'A cherry laurel hedge, a privet hedge and an apple tree are not pruned the same way or in the same month: some species flower on the previous year\'s wood, others tolerate hard cutting, others still go bare at the base if they are trimmed badly. Verde Vercelli identifies the species at the site visit and decides technique and timing plant by plant, so the hedge stays dense from the bottom up, the shrubs flower and the fruit trees bear fruit. For tall trees, where needed, the pruning is done by tree climbing.',
      faq: [
        {
          q: 'When should hedges be trimmed?',
          a: 'It depends on the species: Verde Vercelli sets the timing plant by plant, usually with one main cut and a tidy-up during the growing season. The dates go into the quote or into the scheduled maintenance calendar.',
        },
        {
          q: 'Do you prune fruit trees too?',
          a: 'Yes. Verde Vercelli prunes the fruit trees of gardens in Vercelli and across the province at the right time for each species, to keep the tree balanced and productive.',
        },
        {
          q: 'Can an overgrown hedge be brought down?',
          a: 'Often, yes, with gradual reduction: Verde Vercelli assesses at the site visit how much cutting the species can take and, if needed, spreads the reduction over several seasons so the hedge is not left bare.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Hedge trimming and pruning in Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli trims hedges and prunes shrubs, fruit trees and tall trees in Vercelli and the province, with techniques suited to each species.',
        ogAlt: 'A frame from the Verde Vercelli video on hedge trimming',
      },
    },

    fresatura: {
      name: 'Stump grinding',
      short:
        'Complete stump removal with a professional stump grinder. The ground restored for new planting or for other use of the space.',
      heroTitle: 'Stump grinding in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri removes the stumps left after tree felling in Vercelli and across the province with a professional stump grinder, which goes below ground level without digging a wide hole and returns the area to lawn, new planting or other use.',
      includesTitle: 'What it includes',
      includes: [
        'A check of the area before starting: underground services, irrigation lines, kerbs and nearby low walls.',
        'Grinding the stump below ground level, down to the root collar and the main surface roots.',
        'Removal of the wood chips produced by the grinding.',
        'Filling the hole with topsoil and levelling it.',
        'Reseeding the area or preparing it for new planting.',
      ],
      detailTitle: 'Why the stump should not be left where it is',
      detailText:
        'A stump left in the garden gets in the way of mowing, holds moisture and becomes a way in for fungi and wood-boring insects, while the surface roots keep lifting the ground. Digging it out would mean opening a wide hole and bringing in heavy machinery; Verde Vercelli\'s stump grinding instead works vertically in a small space, turns the stump into wood chips and leaves the area ready for a lawn, a new plant or another use of the space.',
      faq: [
        {
          q: 'How deep does the grinder go?',
          a: 'The Verde Vercelli stump grinder goes below ground level as far as needed to remove the root collar and the main surface roots. The working depth is decided at the site visit, based on what the area will be used for afterwards.',
        },
        {
          q: 'Can a new tree be planted where the stump was?',
          a: 'Yes, but not in exactly the same spot: after grinding, Verde Vercelli fills the hole with topsoil and, if the new plant is a tree, advises moving it a few tens of centimetres away, because decaying woody remains stay in the ground.',
        },
        {
          q: 'Does stump grinding damage the surrounding lawn?',
          a: 'Verde Vercelli\'s stump grinding works on a narrow strip around the stump, so the surrounding lawn stays largely intact. Where the machine leaves marks, the quote includes reseeding.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Stump grinding in Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli grinds stumps below ground level and returns the area to lawn or new planting, in Vercelli and across the province.',
        ogAlt: 'A frame from the Verde Vercelli video on grinding a stump',
      },
    },

    treeClimbing: {
      name: 'Tree climbing',
      short:
        'Work at height using rope-access climbing techniques. Pruning, crown bracing and inspections on large trees.',
      heroTitle: 'Tree climbing in Vercelli: pruning at height',
      heroIntro:
        'Verde Vercelli di Andrea Vetri works on large trees in Vercelli and across the province by tree climbing: the arborist climbs with ropes and a harness and works from inside the crown, even in courtyards and gardens that no vehicle can reach.',
      includesTitle: 'What it includes',
      includes: [
        'Inspection of the tree from the ground and from the crown: trunk, branch unions, cavities and deadwood.',
        'Deadwooding, crown reduction and crown thinning with reduction cuts.',
        'Crown bracing where the limbs need it.',
        'Safety pruning over roofs, lines, entrances and car parks.',
        'Sectional felling with pieces lowered under control.',
        'Collection of branches and clean-up of the work area.',
      ],
      detailTitle: 'Mountaineering skills, applied to trees',
      detailText:
        'Tree climbing is a technical trade that calls for the same skills as mountaineering: knots, anchors, risk management, reading the terrain — in this case, the wood. For Verde Vercelli it is the technique for large trees that stand where no vehicle can go: the only thing that enters the garden is the arborist with the ropes, and working from inside the crown every cut is made in the right place. Technical direction by a national free-climbing instructor and regional mountaineering instructor.',
      faq: [
        {
          q: 'Does tree climbing damage the lawn or the path?',
          a: 'No: Verde Vercelli\'s arborists climb with ropes and a harness and bring no heavy machinery into the garden, so lawns, paths and kerbs are not marked. On the ground we still protect the surfaces where material is lowered.',
        },
        {
          q: 'When should large trees be pruned?',
          a: 'Verde Vercelli carries out routine tree pruning during dormancy, between autumn and late winter. Safety work on unstable branches, on the other hand, is done at any time of year.',
        },
        {
          q: 'Who directs the tree-climbing work?',
          a: 'Verde Vercelli\'s tree-climbing work is directed by a national free-climbing instructor and regional mountaineering instructor, who brings to trees the same rope, anchoring and risk-management techniques used in the mountains.',
        },
        {
          q: 'Do you also work on trees at residential buildings?',
          a: 'Yes. Verde Vercelli carries out pruning and felling by tree climbing on the trees of residential buildings in Vercelli and across the province, with the quote made out to the building and a written summary of the work for the building manager.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Tree climbing and pruning at height | Verde Vercelli',
        description:
          'Pruning, crown bracing and felling by tree climbing on large trees: Verde Vercelli works in Vercelli and across the province.',
        ogAlt: 'A Verde Vercelli arborist tree climbing in a tall tree',
      },
    },

    topiarie: {
      name: 'Topiary',
      short:
        'Artistic sculpting and shaping of hedges and shrubs. Geometric, figurative and decorative forms for gardens with character.',
      heroTitle: 'Topiary in Vercelli',
      heroIntro:
        'Verde Vercelli di Andrea Vetri shapes hedges and shrubs into geometric, figurative and decorative forms in gardens in Vercelli and across the province: from training a new topiary piece to maintaining existing ones, with scheduled trims.',
      includesTitle: 'What it includes',
      includes: [
        'Choosing the shape and the right plant for that spot in the garden.',
        'Training new topiary on young shrubs, guided season after season.',
        'Geometric shapes: spheres, cones, cubes, spirals, square-cut hedges.',
        'Figurative and decorative shapes on request.',
        'Restoring the shape of neglected topiary.',
        'Scheduled maintenance trims and collection of the clippings.',
      ],
      detailTitle: 'A shape is built over time',
      detailText:
        'Topiary is not made in an afternoon: the plant has to be guided gradually, cut after cut, until the shape holds on its own. For each shape Verde Vercelli chooses the species that will carry it — small leaves, compact growth, a good response to pruning — and shapes it by hand, never cutting beyond what the plant can tolerate. The result gives character to a garden, an entrance or a courtyard, and is kept up with regular trims that we schedule together with the maintenance of the rest of the garden.',
      faq: [
        {
          q: 'How long does it take to form a topiary piece?',
          a: 'It depends on the shape and the starting plant: Verde Vercelli shapes topiary gradually, usually over several seasons, and tells you at the site visit how many stages it expects before the shape is complete.',
        },
        {
          q: 'Which plants are suitable for topiary?',
          a: 'Species with small leaves and compact growth that respond well to pruning, such as yew, privet, cherry laurel or hornbeam. Verde Vercelli chooses the plant according to the shape, the aspect and the soil of the garden.',
        },
        {
          q: 'Can you bring a neglected topiary back into shape?',
          a: 'Often, yes: Verde Vercelli assesses at the site visit how far the plant has lost its shape and, if the species allows, restores it with gradual cuts spread over one or more seasons.',
        },
      ],
      relatedTitle: 'Related services',
      ctaLabel: 'Request a free site visit',
      seo: {
        title: 'Topiary in Vercelli | Verde Vercelli',
        description:
          'Verde Vercelli shapes hedges and shrubs into geometric, figurative and decorative forms for gardens, entrances and courtyards in Vercelli.',
        ogAlt: 'A frame from the Verde Vercelli video on an artistic topiary piece',
      },
    },
  },
} satisfies SiteCopy
